/**
 * PythonRenderer - Executa código Python via WebAssembly (Pyodide).
 *
 * Usa dynamic import() do módulo ES (.mjs) em vez de <script src> UMD para
 * garantir que loadPyodide seja disponível no contexto do iframe sandboxed.
 */

export class PythonRenderer {
    static renderToHtml(source: string): string {
        const escaped = source
            .replace(/\\/g, '\\\\')
            .replace(/`/g, '\\`')
            .replace(/\$/g, '\\$');

        return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; font-family: system-ui, -apple-system, sans-serif; background: #0f172a; height: 100%; }
body { padding: 16px; }

.console-wrap   { border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0,0,0,.3), 0 2px 4px -2px rgba(0,0,0,.2); }
.console-header { background: #1e293b; color: #f8fafc; padding: 8px 16px; font-size: 13px; font-weight: 500; display: flex; justify-content: space-between; align-items: center; }
.console-body   { background: #0f172a; padding: 16px; min-height: 140px; max-height: 600px; overflow-y: auto; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 13px; line-height: 1.6; color: #e2e8f0; }

.line-out { color: #e2e8f0; margin: 0; white-space: pre-wrap; word-break: break-all; }
.line-err { color: #f87171; margin: 0; white-space: pre-wrap; word-break: break-all; }
.line-sys { color: #475569; font-style: italic; margin: 0; }

.run-btn { background: #3b82f6; color: #fff; border: none; padding: 4px 14px; border-radius: 4px; font-size: 12px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: background .15s; }
.run-btn:hover:not(:disabled) { background: #2563eb; }
.run-btn:disabled { opacity: .5; cursor: not-allowed; }

.spinner { width: 11px; height: 11px; border: 2px solid #fff; border-bottom-color: transparent; border-radius: 50%; animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Skeleton loader */
.skeleton { display: flex; flex-direction: column; gap: 10px; padding: 4px 0; }
.skel-line {
    height: 13px; border-radius: 4px;
    background: linear-gradient(90deg, #1e3a5f 25%, #3b6fa0 50%, #1e3a5f 75%);
    background-size: 300% 100%;
    animation: shimmer 1.8s ease-in-out infinite;
}
@keyframes shimmer { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
</head>
<body>
<div class="console-wrap">
    <div class="console-header">
        <div style="display:flex;align-items:center;gap:8px;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="4 17 10 11 4 5"></polyline><line x1="12" y1="19" x2="20" y2="19"></line>
            </svg>
            Python Console
        </div>
        <button id="run-btn" class="run-btn" disabled>
            <span class="spinner" id="spinner"></span>
            <span id="btn-text">Initializing…</span>
        </button>
    </div>
    <div class="console-body" id="output">
        <div class="skeleton" id="skeleton">
            <div class="skel-line" style="width:55%"></div>
            <div class="skel-line" style="width:80%"></div>
            <div class="skel-line" style="width:40%"></div>
            <div class="skel-line" style="width:70%"></div>
            <div class="skel-line" style="width:30%"></div>
        </div>
    </div>
</div>

<script type="module">
(async function () {
    var output   = document.getElementById('output');
    var runBtn   = document.getElementById('run-btn');
    var spinner  = document.getElementById('spinner');
    var btnText  = document.getElementById('btn-text');
    var pyReady  = false;
    var py       = null;
    var running  = false;

    var sourceCode = \`${escaped}\`;

    function line(cls, text) {
        var d = document.createElement('div');
        d.className = cls;
        d.textContent = text;
        output.appendChild(d);
        notifyHeight();
        output.scrollTop = output.scrollHeight;
    }

    function notifyHeight() {
        var clone = output.cloneNode(true);
        clone.style.cssText = 'position:absolute;visibility:hidden;height:auto;max-height:none;overflow:visible;';
        document.body.appendChild(clone);
        var natural = clone.scrollHeight;
        document.body.removeChild(clone);

        var total = Math.min(Math.max(natural + 80, 200), 800);
        window.parent.postMessage({
            type: 'artifact-resize',
            height: total,
            sandboxId: window.__sandboxId || ''
        }, '*');
    }

    function setReady() {
        spinner.style.display = 'none';
        btnText.textContent   = 'Run';
        runBtn.disabled       = false;
    }

    function setBusy() {
        spinner.style.display = 'inline-block';
        btnText.textContent   = 'Running…';
        runBtn.disabled       = true;
    }

    async function runCode() {
        if (!pyReady || running) return;
        running = true;
        setBusy();
        var sys = Array.from(output.querySelectorAll('.line-sys'));
        output.innerHTML = '';
        sys.forEach(function (s) { output.appendChild(s); });
        try {
            await py.runPythonAsync(sourceCode);
        } catch (err) {
            line('line-err', err.toString());
        } finally {
            running = false;
            setReady();
            notifyHeight();
        }
    }

    runBtn.addEventListener('click', runCode);

    async function init() {
        try {
            const { loadPyodide } = await import('https://cdn.jsdelivr.net/pyodide/v0.25.1/full/pyodide.mjs');
            py = await loadPyodide({
                stdout: function (msg) { line('line-out', msg); },
                stderr: function (msg) { line('line-err', msg); }
            });
            // Remove skeleton and start fresh
            var skel = document.getElementById('skeleton');
            if (skel) skel.remove();
            pyReady = true;
            setReady();
            runCode();
        } catch (err) {
            var skel = document.getElementById('skeleton');
            if (skel) skel.remove();
            line('line-err', 'Failed to load Pyodide: ' + err.message);
        }
    }

    init();
})();
</script>
</body>
</html>`;
    }
}
